// The current route as React state, and the one function that changes it.
//
// Small enough to read in one go, which is the point: everything about history
// in this application is these thirty lines, so there is one place where a
// navigation could fail to update the screen.
//
// `popstate` is subscribed rather than polled. A browser back button that does
// not redraw is the specific bug hand-rolled routing is known for, and it is
// invisible in development because nobody presses back on a page they are
// building.

import { useCallback, useEffect, useState } from "react";
import { pathFor, routeFor, type Route } from "./route.js";

/**
 * `replace` swaps the entry in hand rather than adding one. Its one caller is
 * a kiosk item whose crew shortcut is being retyped (C9): the address follows
 * the id as it is typed, and a history entry per keystroke would make Back
 * walk the word backwards a letter at a time.
 */
export type Navigate = (route: Route, how?: { readonly replace?: boolean }) => void;

export function useRoute(): { route: Route; navigate: Navigate } {
  const [route, setRoute] = useState<Route>(() => routeFor(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(routeFor(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback<Navigate>((next, how) => {
    const path = pathFor(next);
    if (how?.replace === true) {
      window.history.replaceState(null, "", path);
      setRoute(next);
      return;
    }
    // pushState rather than replaceState, so back works. The one place that
    // wants replace is landing on the login screen after a session ends, and it
    // is deliberately not special-cased: somebody pressing back from there gets
    // the screen they were on, which redirects them straight back here. A
    // replace would instead take them out of the console entirely, to whatever
    // they were looking at before, which reads as the console having crashed.
    window.history.pushState(null, "", path);
    setRoute(next);
  }, []);

  return { route, navigate };
}
